import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { MenuOutlined, BellOutlined } from '@ant-design/icons';
import { withSnackbar } from 'notistack';
import { Badge, Button, Drawer, List, Spin } from 'antd';
import Rest from 'tools/rest';
import { ActionCableConsumer } from '@thrash-industries/react-actioncable-provider';
import { initWebPush } from 'tools/web_push_client';

class Header extends Component {
  state = {
    notificationsVisible: false,
    notifications: [],
    notificationsPage: 1,
    notificationsHasNextPage: true,
    notificationsLoading: false,
    notificationsUnreadCount: 0,
    webPushInitRequested: false,
  };

  componentDidMount() {
    this.loadNotifications({ page: 1, append: false });
  }

  handleOpenNotifications = () => {
    if (!this.state.webPushInitRequested) {
      this.setState({ webPushInitRequested: true });
      initWebPush();
    }
    this.setState({ notificationsVisible: true }, () => {
      if (this.state.notifications.length === 0) {
        this.loadNotifications({ page: 1, append: false });
      }
    });
  };

  markNotificationRead = (id) => {
    return Rest.patch(`/api/v1/user_notifications/${id}/read.json`)
      .then(() => {
        this.setState((prevState) => {
          let unreadDelta = 0;
          const notifications = prevState.notifications.map((item) => {
            if (item.id !== id) return item;
            if (item.status === 'created') unreadDelta = 1;
            return { ...item, status: 'readed' };
          });
          return {
            notifications,
            notificationsUnreadCount: Math.max(prevState.notificationsUnreadCount - unreadDelta, 0),
          };
        });
      })
      .catch(() => {});
  };

  getNotificationTargetPath = (item) => {
    if (!item || !item.model_type || !item.model_id) {
      return null;
    }

    const modelId = item.model_id;
    const isMobileShell = /^\/m(?:\/|$)/.test(window.location.pathname);

    switch (item.model_type) {
      case 'Request':
        return isMobileShell ? `/m/requests/${modelId}` : `/help_desk?number=${modelId}`;
      case 'Expense':
        return isMobileShell ? `/m/expenses/${modelId}` : `/expense_manager?number=${modelId}`;
      case 'LbAgreement':
        return isMobileShell ? `/m/agreements/${modelId}` : `/agreements?number=${modelId}`;
      case 'TeledomRequest':
        return isMobileShell ? '/m' : `/teledom_requests?number=${modelId}`;
      case 'BlockingService':
        return isMobileShell ? '/m' : `/blocking_services?blocking_number=${modelId}`;
      default:
        return null;
    }
  };

  handleNotificationClick = (item) => {
    const targetPath = this.getNotificationTargetPath(item);
    this.markNotificationRead(item.id).finally(() => {
      if (targetPath) {
        window.location.assign(targetPath);
      }
    });
  };

  markAllNotificationsRead = () => {
    Rest.patch('/api/v1/user_notifications/read_all.json')
      .then(() => {
        this.setState((prevState) => ({
          notifications: prevState.notifications.map((item) => ({ ...item, status: 'readed' })),
          notificationsUnreadCount: 0,
        }));
      })
      .catch(() => {});
  };

  loadNotifications = ({ page = 1, append = false } = {}) => {
    if (this.state.notificationsLoading) {
      return;
    }

    this.setState({ notificationsLoading: true });
    Rest.get('/api/v1/user_notifications.json', {
      params: { page, per: 20 },
    })
      .then((response) => {
        const payload = response.data || {};
        const rows = payload.notifications || [];
        const pagination = payload.pagination || {};

        this.setState((prevState) => ({
          notifications: append ? [...prevState.notifications, ...rows] : rows,
          notificationsPage: page,
          notificationsHasNextPage: !!pagination.has_next_page,
          notificationsUnreadCount: payload.unread_count || 0,
          notificationsLoading: false,
        }));
      })
      .catch(() => {
        this.setState({ notificationsLoading: false });
      });
  };

  handleNotificationsScroll = (event) => {
    const target = event.target;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 32;
    if (!nearBottom || this.state.notificationsLoading || !this.state.notificationsHasNextPage) {
      return;
    }

    this.loadNotifications({ page: this.state.notificationsPage + 1, append: true });
  };

  handleRealtimeNotification = () => {
    this.loadNotifications({ page: 1, append: false });
  };

  render() {
    const { classes, openMenu, user } = this.props;
    const auth = !!user;

    return (
      <div className={classes.mobile_header}>
        {auth && (
          <ActionCableConsumer
            channel={{ channel: 'UserNotificationsChannel' }}
            onReceived={this.handleRealtimeNotification}
          />
        )}
        <MenuOutlined onClick={openMenu} className={classes.menu} />
        {/* <BellOutlined onClick={this.handleOpenNotifications} className={classes.bell} /> */}
        {auth && (
          <Button
            type="text"
            className={classes.bellButton}
            onClick={this.handleOpenNotifications}
            icon={
              <Badge className={classes.bell} count={this.state.notificationsUnreadCount} size="small" overflowCount={99}>
                <BellOutlined className={classes.bell} />
              </Badge>
            }
          />
        )}
        <Drawer
          open={this.state.notificationsVisible}
          title="Уведомления"
          onClose={() => { this.setState({ notificationsVisible: false }); }}
          width="85vw"
          maskClosable
          placement="right"
          extra={
            <Button type="link" onClick={this.markAllNotificationsRead}>
              Прочитать все
            </Button>
          }
          bodyStyle={{ padding: '8px 16px', height: '100%', overflow: 'hidden' }}
        >
          <div onScroll={this.handleNotificationsScroll} style={{ height: '100%', overflowY: 'auto' }}>
            <List
              dataSource={this.state.notifications}
              locale={{ emptyText: 'Нет уведомлений' }}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  style={{ cursor: 'pointer', background: item.status === 'created' ? '#fff7e6' : 'transparent' }}
                  onClick={() => this.handleNotificationClick(item)}
                >
                  <List.Item.Meta
                    title={
                      <span style={{ fontWeight: item.status === 'created' ? 600 : 400 }}>
                        {item.title}
                      </span>
                    }
                    description={
                      <div>
                        <div>{item.body}</div>
                        <small style={{ color: '#8c8c8c' }}>
                          {new Date(item.created_at).toLocaleString('ru-RU')}
                        </small>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            {this.state.notificationsLoading && (
              <div style={{ textAlign: 'center', padding: 12 }}>
                <Spin size="small" />
              </div>
            )}
          </div>
        </Drawer>
      </div>
    );
  }
}

const styles = () => ({
  mobile_header: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    padding: '0 12px',
    position: 'relative',
    zIndex: 2,
  },
  menu: {
    fontSize: '30px',
    color: '#59059B',
  },
  bellButton: {
    padding: 0,
    height: 'auto',
    lineHeight: 1,
    fontSize: '30px',
    '& .anticon': {
      fontSize: '30px',
    },
  },
  bell: {
    fontSize: '30px',
    color: '#59059B',
  },
});

const mapStateToProps = (state) => {
  return { user: state.user };
};

export default connect(
  mapStateToProps,
  null,
)(withStyles(styles)(withSnackbar(Header)));
